/**
 * Layered graph layout (Sugiyama), used to place a whole diagram at once.
 *
 * The incremental tools place one box at a time, which cannot avoid crossings:
 * at the moment a box is positioned, the edges it will eventually carry do not
 * exist yet, so there is nothing to optimise against. Every diagramming tool
 * that produces clean output solves this the same way — Mermaid (and therefore
 * Claude's diagrams) hands the whole graph to dagre, and the Excalidraw MCP
 * servers use Sugiyama or grandalf. The model declares STRUCTURE; the engine
 * decides GEOMETRY.
 *
 * The pipeline is the standard one:
 *   1. break cycles so the rest can assume a DAG
 *   2. assign nodes to layers
 *   3. insert dummy nodes so a long edge occupies space in the layers it spans
 *   4. order within layers to minimise crossings (median + transpose)
 *   5. assign coordinates
 *
 * Step 3 is what stops arrows cutting through boxes. Excalidraw draws a bound
 * arrow as a straight line, so an edge spanning three layers will slice through
 * whatever sits between its endpoints — unless a dummy reserves a corridor
 * there and pushes the real boxes aside.
 *
 * Pure module: no Excalidraw imports, no DOM. Everything here is unit testable.
 */

export type LayoutNode = {
  key: string;
  width: number;
  height: number;
};

export type LayoutEdge = {
  from: string;
  to: string;
};

export type LayoutDirection = "TB" | "LR";

export type LayoutOptions = {
  direction?: LayoutDirection;
  /** Space between one layer and the next. */
  layerGap?: number;
  /** Space between siblings within a layer. */
  nodeGap?: number;
  originX?: number;
  originY?: number;
};

export type Placement = { x: number; y: number };

export type LayoutResult = {
  placements: Map<string, Placement>;
  /**
   * Bend points for each input edge, in input order — the corridors its dummy
   * nodes reserved. Empty for an edge between adjacent layers, which needs no
   * detour. Drawing an arrow through these is what keeps a long edge off the
   * boxes it passes; a straight segment cannot be relied on to stay inside a
   * corridor once several long edges converge on the same node.
   */
  routes: Placement[][];
  /**
   * Which layer each node landed in. Callers colour by tier, and the layers
   * already are the tiers — clients, gateway, services, stores — so this is
   * the natural thing to key a palette off.
   */
  layers: Map<string, number>;
};

const DEFAULTS = {
  direction: "TB" as LayoutDirection,
  layerGap: 120,
  nodeGap: 70,
  originX: 200,
  originY: 120,
};

/** Covers both real boxes and the dummies that reserve corridors. */
type Slot = {
  key: string;
  width: number;
  height: number;
  layer: number;
  order: number;
  /** Position along the layer axis (x when TB, y when LR). */
  pos: number;
  isDummy: boolean;
};

const edgeId = (from: string, to: string) => `${from} ${to}`;

/**
 * Reverse the edges that close a cycle, so layering has a DAG to work with.
 * The reversal is layout-only — the caller still draws the original direction.
 */
const breakCycles = (nodeKeys: string[], edges: LayoutEdge[]) => {
  const outgoing = new Map<string, string[]>();
  for (const key of nodeKeys) {
    outgoing.set(key, []);
  }
  for (const edge of edges) {
    outgoing.get(edge.from)?.push(edge.to);
  }

  const state = new Map<string, "unvisited" | "onStack" | "done">();
  for (const key of nodeKeys) {
    state.set(key, "unvisited");
  }
  const reversed = new Set<string>();

  const visit = (key: string) => {
    state.set(key, "onStack");
    for (const next of outgoing.get(key) ?? []) {
      const nextState = state.get(next);
      if (nextState === "onStack") {
        reversed.add(edgeId(key, next)); // back edge: this closes the cycle
      } else if (nextState === "unvisited") {
        visit(next);
      }
    }
    state.set(key, "done");
  };

  for (const key of nodeKeys) {
    if (state.get(key) === "unvisited") {
      visit(key);
    }
  }

  return edges.map((edge) =>
    reversed.has(edgeId(edge.from, edge.to))
      ? { from: edge.to, to: edge.from, wasReversed: true }
      : { from: edge.from, to: edge.to, wasReversed: false },
  );
};

/** Longest-path layering: a node sits one layer below its deepest parent. */
const assignLayers = (
  nodeKeys: string[],
  edges: { from: string; to: string }[],
) => {
  const parents = new Map<string, string[]>();
  const children = new Map<string, string[]>();
  for (const key of nodeKeys) {
    parents.set(key, []);
    children.set(key, []);
  }
  for (const edge of edges) {
    if (edge.from === edge.to) {
      continue;
    }
    children.get(edge.from)?.push(edge.to);
    parents.get(edge.to)?.push(edge.from);
  }

  const layer = new Map<string, number>();
  const indegree = new Map<string, number>();
  for (const key of nodeKeys) {
    indegree.set(key, parents.get(key)!.length);
  }

  const queue = nodeKeys.filter((key) => indegree.get(key) === 0);
  for (const key of queue) {
    layer.set(key, 0);
  }

  // Kahn's algorithm; cycles are already broken so this drains completely.
  for (let i = 0; i < queue.length; i++) {
    const key = queue[i];
    for (const child of children.get(key) ?? []) {
      layer.set(
        child,
        Math.max(layer.get(child) ?? 0, (layer.get(key) ?? 0) + 1),
      );
      indegree.set(child, (indegree.get(child) ?? 0) - 1);
      if (indegree.get(child) === 0) {
        queue.push(child);
      }
    }
  }

  for (const key of nodeKeys) {
    if (!layer.has(key)) {
      layer.set(key, 0);
    }
  }

  return layer;
};

/** Median of a node's neighbour positions in the adjacent layer. */
const medianOf = (positions: number[]) => {
  if (positions.length === 0) {
    return -1;
  }
  const sorted = [...positions].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1
    ? sorted[mid]
    : (sorted[mid - 1] + sorted[mid]) / 2;
};

const countCrossingsBetween = (
  upper: Slot[],
  lower: Slot[],
  adjacency: Map<string, Set<string>>,
) => {
  const lowerIndex = new Map(lower.map((slot, index) => [slot.key, index]));
  const pairs: number[] = [];
  for (const slot of upper) {
    const targets = [...(adjacency.get(slot.key) ?? [])]
      .map((key) => lowerIndex.get(key))
      .filter((index): index is number => index !== undefined)
      .sort((a, b) => a - b);
    pairs.push(...targets);
  }

  let crossings = 0;
  for (let i = 0; i < pairs.length; i++) {
    for (let j = i + 1; j < pairs.length; j++) {
      if (pairs[i] > pairs[j]) {
        crossings++;
      }
    }
  }
  return crossings;
};

export const layoutGraph = (
  nodes: LayoutNode[],
  edges: LayoutEdge[],
  options: LayoutOptions = {},
): LayoutResult => {
  const { direction, layerGap, nodeGap, originX, originY } = {
    ...DEFAULTS,
    ...options,
  };

  const placements = new Map<string, Placement>();
  const routes: Placement[][] = edges.map(() => []);
  const nodeLayers = new Map<string, number>();
  if (nodes.length === 0) {
    return { placements, routes, layers: nodeLayers };
  }

  const known = new Set(nodes.map((node) => node.key));
  // Keep the original index so routes come back aligned with the caller's list.
  const usable = edges
    .map((edge, index) => ({ ...edge, index }))
    .filter(
      (edge) =>
        known.has(edge.from) && known.has(edge.to) && edge.from !== edge.to,
    );
  const usableEdges = usable.map(({ from, to }) => ({ from, to }));

  // ---- 1. cycles ---------------------------------------------------------
  const acyclic = breakCycles(
    nodes.map((node) => node.key),
    usableEdges,
  );

  // ---- 2. layers ---------------------------------------------------------
  const layerOf = assignLayers(
    nodes.map((node) => node.key),
    acyclic,
  );

  // ---- 3. dummies --------------------------------------------------------
  // A long edge gets a placeholder in every layer it passes over, so those
  // layers reserve room for it instead of packing boxes into its path.
  const slots: Slot[] = nodes.map((node) => ({
    key: node.key,
    width: node.width,
    height: node.height,
    layer: layerOf.get(node.key) ?? 0,
    order: 0,
    pos: 0,
    isDummy: false,
  }));

  const adjacency = new Map<string, Set<string>>();
  const link = (from: string, to: string) => {
    if (!adjacency.has(from)) {
      adjacency.set(from, new Set());
    }
    adjacency.get(from)!.add(to);
  };

  let dummyCount = 0;
  // dummy chain per original edge index, ordered from its lower layer upward
  const chainKeys = new Map<number, string[]>();

  for (let i = 0; i < acyclic.length; i++) {
    const edge = acyclic[i];
    const originalIndex = usable[i].index;
    const fromLayer = layerOf.get(edge.from)!;
    const toLayer = layerOf.get(edge.to)!;

    if (toLayer - fromLayer <= 1) {
      link(edge.from, edge.to);
      continue;
    }

    const chain: string[] = [];
    let previous = edge.from;
    for (let layer = fromLayer + 1; layer < toLayer; layer++) {
      const dummyKey = ` dummy${dummyCount++}`;
      slots.push({
        key: dummyKey,
        // Narrow but not zero: the corridor needs real width to keep boxes off
        // the line, without shoving the layer apart more than necessary.
        width: 60,
        height: 8,
        layer,
        order: 0,
        pos: 0,
        isDummy: true,
      });
      chain.push(dummyKey);
      link(previous, dummyKey);
      previous = dummyKey;
    }
    link(previous, edge.to);

    // A reversed edge was laid out backwards, so its corridors run the other
    // way relative to the direction the caller will actually draw.
    chainKeys.set(
      originalIndex,
      edge.wasReversed ? [...chain].reverse() : chain,
    );
  }

  // ---- 4. ordering -------------------------------------------------------
  const maxLayer = Math.max(...slots.map((slot) => slot.layer));
  let layers: Slot[][] = [];
  for (let i = 0; i <= maxLayer; i++) {
    layers.push(slots.filter((slot) => slot.layer === i));
  }

  const applyOrder = (layer: Slot[]) =>
    layer.forEach((slot, index) => {
      slot.order = index;
    });
  layers.forEach(applyOrder);

  const reverseAdjacency = new Map<string, Set<string>>();
  for (const [from, targets] of adjacency) {
    for (const to of targets) {
      if (!reverseAdjacency.has(to)) {
        reverseAdjacency.set(to, new Set());
      }
      reverseAdjacency.get(to)!.add(from);
    }
  }

  const totalCrossings = () => {
    let total = 0;
    for (let i = 0; i + 1 < layers.length; i++) {
      total += countCrossingsBetween(layers[i], layers[i + 1], adjacency);
    }
    return total;
  };

  const medianSweep = (downward: boolean) => {
    const indices = downward
      ? layers.map((_, i) => i).slice(1)
      : layers
          .map((_, i) => i)
          .slice(0, -1)
          .reverse();

    for (const index of indices) {
      const fixed = downward ? layers[index - 1] : layers[index + 1];
      const fixedIndex = new Map(fixed.map((slot, i) => [slot.key, i]));

      const scored = layers[index].map((slot, fallback) => {
        const related = downward
          ? [...(reverseAdjacency.get(slot.key) ?? [])]
          : [...(adjacency.get(slot.key) ?? [])];
        const positions = related
          .map((key) => fixedIndex.get(key))
          .filter((value): value is number => value !== undefined);
        return { slot, median: medianOf(positions), fallback };
      });

      // A node with no neighbour in the fixed layer keeps its place, which
      // stops it drifting to the front and dragging its own edges across.
      scored.sort((a, b) => {
        const aKey = a.median === -1 ? a.fallback : a.median;
        const bKey = b.median === -1 ? b.fallback : b.median;
        return aKey - bKey || a.fallback - b.fallback;
      });

      layers[index] = scored.map((entry) => entry.slot);
      applyOrder(layers[index]);
    }
  };

  /** Swap adjacent pairs while it strictly reduces crossings. */
  const transpose = () => {
    let improved = true;
    let guard = 0;
    while (improved && guard++ < 8) {
      improved = false;
      for (let i = 0; i < layers.length; i++) {
        for (let j = 0; j + 1 < layers[i].length; j++) {
          const before = totalCrossings();
          [layers[i][j], layers[i][j + 1]] = [layers[i][j + 1], layers[i][j]];
          applyOrder(layers[i]);
          if (totalCrossings() < before) {
            improved = true;
          } else {
            [layers[i][j], layers[i][j + 1]] = [layers[i][j + 1], layers[i][j]];
            applyOrder(layers[i]);
          }
        }
      }
    }
  };

  let best = layers.map((layer) => [...layer]);
  let bestScore = totalCrossings();

  for (let iteration = 0; iteration < 4; iteration++) {
    medianSweep(iteration % 2 === 0);
    transpose();
    const score = totalCrossings();
    if (score < bestScore) {
      bestScore = score;
      best = layers.map((layer) => [...layer]);
    }
    if (bestScore === 0) {
      break;
    }
  }

  layers = best;
  layers.forEach(applyOrder);

  // ---- 5. coordinates ----------------------------------------------------
  const sizeAlong = (slot: Slot) =>
    direction === "TB" ? slot.width : slot.height;
  const sizeAcross = (slot: Slot) =>
    direction === "TB" ? slot.height : slot.width;

  const byKey = new Map(slots.map((slot) => [slot.key, slot]));

  const packLayer = (layer: Slot[]) => {
    let cursor = 0;
    for (const slot of layer) {
      slot.pos = cursor;
      cursor += sizeAlong(slot) + nodeGap;
    }
  };
  layers.forEach(packLayer);

  // Pull nodes toward the median of their neighbours, then restore spacing.
  //
  // Neighbours across a long edge (i.e. dummies) are weighted far more heavily
  // than ordinary ones. Without that bias, a node feeding both a neighbour and
  // a long edge settles between the two — which parks it directly over the box
  // in the next layer, and the straight arrow then clips that box. Letting the
  // long edge win straightens the chain over its reserved corridor instead,
  // which is the whole reason the corridor exists.
  const CHAIN_WEIGHT = 4;

  for (let pass = 0; pass < 4; pass++) {
    for (const layer of layers) {
      for (const slot of layer) {
        const related = [
          ...(adjacency.get(slot.key) ?? []),
          ...(reverseAdjacency.get(slot.key) ?? []),
        ];

        const centres: number[] = [];
        for (const key of related) {
          const neighbour = byKey.get(key);
          if (!neighbour) {
            continue;
          }
          const centre = neighbour.pos + sizeAlong(neighbour) / 2;
          const weight = neighbour.isDummy || slot.isDummy ? CHAIN_WEIGHT : 1;
          for (let i = 0; i < weight; i++) {
            centres.push(centre);
          }
        }

        if (centres.length) {
          slot.pos = medianOf(centres) - sizeAlong(slot) / 2;
        }
      }

      layer.sort((a, b) => a.pos - b.pos);
      applyOrder(layer);
      for (let i = 1; i < layer.length; i++) {
        const minimum = layer[i - 1].pos + sizeAlong(layer[i - 1]) + nodeGap;
        if (layer[i].pos < minimum) {
          layer[i].pos = minimum;
        }
      }
    }
  }

  const minPos = Math.min(...slots.map((slot) => slot.pos));
  const layerOffsets: number[] = [];
  let across = 0;
  for (const layer of layers) {
    layerOffsets.push(across);
    const thickest = Math.max(0, ...layer.map(sizeAcross));
    across += thickest + layerGap;
  }

  for (const slot of slots) {
    if (slot.isDummy) {
      continue;
    }
    const along = slot.pos - minPos;
    const acrossPos = layerOffsets[slot.layer];
    placements.set(
      slot.key,
      direction === "TB"
        ? { x: Math.round(originX + along), y: Math.round(originY + acrossPos) }
        : {
            x: Math.round(originX + acrossPos),
            y: Math.round(originY + along),
          },
    );
  }

  const centreOfSlot = (slot: Slot): Placement => {
    const along = slot.pos - minPos + sizeAlong(slot) / 2;
    const thickest = Math.max(0, ...layers[slot.layer].map(sizeAcross));
    const acrossPos = layerOffsets[slot.layer] + thickest / 2;
    return direction === "TB"
      ? { x: Math.round(originX + along), y: Math.round(originY + acrossPos) }
      : { x: Math.round(originX + acrossPos), y: Math.round(originY + along) };
  };

  for (const [index, keys] of chainKeys) {
    routes[index] = keys
      .map((key) => byKey.get(key))
      .filter((slot): slot is Slot => Boolean(slot))
      .map(centreOfSlot);
  }

  for (const slot of slots) {
    if (!slot.isDummy) {
      nodeLayers.set(slot.key, slot.layer);
    }
  }

  return { placements, routes, layers: nodeLayers };
};
